// src/components/upgradeNotice.js
import React, { useState } from 'react';
import { X, Star } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { getBrazilianStatus } from '../utils/detectBrazil';
import { getUserLanguage, SUPPORTED_LANGUAGES } from '../utils/detectLanguage';
import { upgradeNoticePtBR } from './mappings/upgradeNotice.pt-br';
import { upgradeNoticeES } from './mappings/upgradeNotice.es';

const UpgradeNotice = ({
  hoursUntilReset,
  onClose,
  isMobile = false,
  tier = 'basic',
  messageCount = 0,
  dailyLimit = 3
}) => {
  const navigate = useNavigate();
  const [isVisible, setIsVisible] = useState(true);

  // Development mode test button state
  const [showTestButton] = useState(process.env.REACT_APP_ENVIRONMENT === 'development');
  
  // Check if user is Brazilian
  const isBrazilian = getBrazilianStatus();

  // Get user language
  const userLanguage = getUserLanguage();

  // Translation function
  const t = (text) => {
    if (userLanguage === SUPPORTED_LANGUAGES.PT_BR) {
      return upgradeNoticePtBR[text] || text;
    }
    if (userLanguage === SUPPORTED_LANGUAGES.ES || userLanguage === SUPPORTED_LANGUAGES.ES_MX) {
      return upgradeNoticeES[text] || text;
    }
    return text; // Default English
  };

  const handleClose = () => {
    setIsVisible(false);
    if (onClose) {
      setTimeout(onClose, 300); // Delay to allow exit animation to complete
    }
  };

  const handleUpgrade = () => {
    navigate('/upgrade');
  };
  
  const handleBuyStarCoins = () => {
    // Dispatch a custom event that StarCoinsDisplay can listen to
    window.dispatchEvent(new CustomEvent('openStarCoinsModal'));
    handleClose();
  };

  // Determine if user is pro
  const isPro = tier === 'pro';
  
  // Different messaging based on tier
  const title = t('Limit Hit');

  // Get localized body text with dynamic replacement
  const getBodyText = () => {
    if (userLanguage === SUPPORTED_LANGUAGES.PT_BR) {
      return upgradeNoticePtBR['body_text'].replace('{hoursUntilReset}', Math.ceil(hoursUntilReset || 12));
    }
    if (userLanguage === SUPPORTED_LANGUAGES.ES || userLanguage === SUPPORTED_LANGUAGES.ES_MX) {
      return upgradeNoticeES['body_text'].replace('{hoursUntilReset}', Math.ceil(hoursUntilReset || 12));
    }
    return `You've maxed out today's free plays.\nYour balance resets in ${Math.ceil(hoursUntilReset || 12)} hours.`;
  };

  const description = getBodyText();

  // Get localized offer text
  const getOfferText = () => {
    if (userLanguage === SUPPORTED_LANGUAGES.PT_BR) {
      return upgradeNoticePtBR['offer_section'];
    }
    if (userLanguage === SUPPORTED_LANGUAGES.ES || userLanguage === SUPPORTED_LANGUAGES.ES_MX) {
      return upgradeNoticeES['offer_section'];
    }
    return `Just 11 StarCoins → unlock 2x more AI breakdowns or 3x QuickPicks\nGrab StarCoins to keep spinning!\nGo Pro for 40x AI chats every day.`;
  };

  const offerText = getOfferText();

  // For mobile, show a banner at the top
  if (isMobile) {
    return (
      <AnimatePresence>
        {isVisible && (
          <motion.div
            className="fixed top-14 left-0 right-0 z-30 px-4 py-3"
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.3 }}
          >
            <div className="max-w-md mx-auto bg-black backdrop-blur-sm p-4 border border-red-500/30">
              <div className="flex items-start justify-between">
                <div className="flex items-center">
                  <div className="border-l border-red-500/50 pl-3">
                    <span className="text-red-400/90 font-medium text-sm uppercase tracking-wider">{title}</span>
                  </div>
                </div>
                <button
                  onClick={handleClose}
                  className="text-white/50 hover:text-white/80 p-1 transition-all hover:bg-white/[0.02]"
                  aria-label={t("Close")}
                >
                  <X className="w-4 h-4" />
                </button>
              </div>

              <p className="text-white/50 text-sm mt-2 whitespace-pre-line">
                {description}
              </p>

              {/* Star Coins - Primary option */}
              <button
                onClick={handleBuyStarCoins}
                className="w-full mt-3 bg-amber-500/20 border border-amber-500/40 hover:bg-amber-500/30 hover:border-amber-500/60 text-amber-200 font-medium py-2 px-3 text-sm backdrop-blur-sm transition-all flex items-center justify-center gap-1.5"
              >
                <Star className="w-4 h-4" />
                {t('Buy Star Coins to Continue')}
              </button>

              {/* Upgrade button - TEMPORARILY DISABLED */}
              {/* {!isPro && (
                <button
                  onClick={handleUpgrade}
                  className="w-full mt-2 bg-emerald-500 hover:bg-emerald-600 text-white font-medium py-1.5 px-3 text-sm rounded-md transition-colors"
                >
                  {t('Upgrade to Pro')}
                </button>
              )} */}

              {/* Offer text - TEMPORARILY DISABLED (Pro mention) */}
              {/* <p className="text-xs text-center mt-2 text-gray-400 whitespace-pre-line">
                {offerText}
              </p> */}

              <p className="text-xs text-center mt-2 text-white/30 font-mono">
                {t('Your call. Your game.')}
              </p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    );
  }

  // Desktop version (non-blocking notification)
  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          className="fixed bottom-6 right-6 z-40 max-w-md"
          initial={{ opacity: 0, y: 20, scale: 0.95 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 20, scale: 0.95 }}
          transition={{ type: "spring", damping: 20, stiffness: 300 }}
        >
          <div className="bg-black backdrop-blur-sm p-6 border border-red-500/30">
            <div className="flex items-start justify-between mb-3">
              <div className="flex items-center">
                <div className="border-l border-red-500/50 pl-3">
                  <h3 className="text-lg font-medium text-red-400/90 uppercase tracking-wider">
                    {title}
                  </h3>
                </div>
              </div>
              <button
                onClick={handleClose}
                className="p-1 transition-all hover:bg-white/[0.02] text-white/50 hover:text-white/80"
                aria-label="Close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <p className="text-white/50 mb-4 whitespace-pre-line">
              {description}
            </p>

            {/* Star Coins - Primary CTA */}
            <div className="mb-4">
              <button
                onClick={handleBuyStarCoins}
                className="w-full bg-amber-500/20 border border-amber-500/40 hover:bg-amber-500/30 hover:border-amber-500/60 text-amber-200 text-sm font-medium py-3 px-4 backdrop-blur-sm transition-all flex items-center justify-center gap-2"
              >
                <Star className="w-4 h-4" />
                {t('Buy Star Coins to Continue')}
              </button>
            </div>

            {/* Close button */}
            <div className="flex justify-center">
              <button
                onClick={handleClose}
                className="py-2 px-6 border border-violet-500/30 text-white/70 hover:text-violet-300/80 hover:border-violet-400/50 transition-all text-sm font-medium"
              >
                {t('Got it')}
              </button>
            </div>

            {/* TEMPORARILY DISABLED - Pro upgrade buttons */}
            {/* {isPro ? (
              // Pro users - just close button
              <div className="flex justify-center">
                <button
                  onClick={handleClose}
                  className="py-2 px-6 border border-emerald-400/20 text-emerald-300 hover:bg-emerald-500/10 rounded-md transition-colors text-sm font-medium"
                >
                  {t('Got it')}
                </button>
              </div>
            ) : (
              // Basic users - upgrade + continue options
              <div className="flex space-x-3">
                <button
                  onClick={handleClose}
                  className="flex-1 py-2 px-4 border border-emerald-400/20 text-emerald-300 hover:bg-emerald-500/10 rounded-md transition-colors text-sm font-medium"
                >
                  {t('Continue in Basic')}
                </button>
                <button
                  onClick={handleUpgrade}
                  className="flex-1 py-2 px-4 bg-emerald-500 hover:bg-emerald-600 text-white rounded-md transition-colors text-sm font-medium"
                >
                  {t('Upgrade to Pro')}
                </button>
              </div>
            )} */}

            {/* TEMPORARILY DISABLED - Offer text with Pro mention */}
            {/* <p className="text-xs text-center mt-3 text-gray-400 whitespace-pre-line">
              {offerText}
            </p> */}

            <p className="text-xs text-center mt-3 text-white/30 font-mono">
              {t('Your call. Your game.')}
            </p>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

// Test wrapper component for development mode
export const UpgradeNoticeTestButton = () => {
  const [showNotice, setShowNotice] = useState(false);
  const [testConfig, setTestConfig] = useState({
    isMobile: false,
    tier: 'basic',
    hoursUntilReset: 12,
    messageCount: 3,
    dailyLimit: 3
  });

  if (process.env.REACT_APP_ENVIRONMENT !== 'development') {
    return null;
  }

  return (
    <>
      {/* Floating test button */}
      <motion.div
        className="fixed bottom-6 left-6 z-50"
        initial={{ opacity: 0, scale: 0.8 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ delay: 0.5 }}
      >
        <div className="bg-purple-600/90 backdrop-blur-sm rounded-lg p-3 shadow-lg border border-purple-400/30">
          <p className="text-white text-xs font-medium mb-2">Dev Tools - UpgradeNotice</p>

          {/* Tier selector */}
          <div className="mb-2">
            <label className="text-white text-xs block mb-1">Tier:</label>
            <select
              value={testConfig.tier}
              onChange={(e) => setTestConfig({ ...testConfig, tier: e.target.value })}
              className="w-full text-xs px-2 py-1 rounded bg-purple-700 text-white border border-purple-400/30"
            >
              <option value="basic">Basic</option>
              <option value="pro">Pro</option>
            </select>
          </div>

          {/* Mobile toggle */}
          <div className="mb-2">
            <label className="flex items-center text-white text-xs cursor-pointer">
              <input
                type="checkbox"
                checked={testConfig.isMobile}
                onChange={(e) => setTestConfig({ ...testConfig, isMobile: e.target.checked })}
                className="mr-2"
              />
              Mobile Mode
            </label>
          </div>

          {/* Hours until reset */}
          <div className="mb-2">
            <label className="text-white text-xs block mb-1">Hours Until Reset:</label>
            <input
              type="number"
              value={testConfig.hoursUntilReset}
              onChange={(e) => setTestConfig({ ...testConfig, hoursUntilReset: parseInt(e.target.value) })}
              className="w-full text-xs px-2 py-1 rounded bg-purple-700 text-white border border-purple-400/30"
              min="0"
              max="24"
            />
          </div>

          {/* Daily limit */}
          <div className="mb-3">
            <label className="text-white text-xs block mb-1">Daily Limit:</label>
            <input
              type="number"
              value={testConfig.dailyLimit}
              onChange={(e) => setTestConfig({ ...testConfig, dailyLimit: parseInt(e.target.value) })}
              className="w-full text-xs px-2 py-1 rounded bg-purple-700 text-white border border-purple-400/30"
              min="1"
              max="100"
            />
          </div>

          {/* Show/Hide button */}
          <button
            onClick={() => setShowNotice(!showNotice)}
            className="w-full bg-purple-500 hover:bg-purple-400 text-white text-xs font-medium py-2 px-3 rounded transition-colors"
          >
            {showNotice ? 'Hide Notice' : 'Show Notice'}
          </button>
        </div>
      </motion.div>

      {/* The actual notice */}
      {showNotice && (
        <UpgradeNotice
          hoursUntilReset={testConfig.hoursUntilReset}
          onClose={() => setShowNotice(false)}
          isMobile={testConfig.isMobile}
          tier={testConfig.tier}
          messageCount={testConfig.messageCount}
          dailyLimit={testConfig.dailyLimit}
        />
      )}
    </>
  );
};

export default UpgradeNotice;
